import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Phone, Mail, Instagram, Facebook, ChevronDown, ChevronUp, Send, MessageCircle, Clock } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
const TikTokIcon = ({
  size = 20
}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-644">
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" data-id="element-645" />
  </svg>;
const faqData = [{
  question: 'What are your shipping times?',
  answer: 'Standard shipping typically takes 5-7 business days. International orders may take 10-14 business days depending on the destination.'
}, {
  question: 'How do I process a refund?',
  answer: 'We offer a 30-day return policy. Please visit our Returns portal with your order number and email to start the process.'
}, {
  question: 'How do digital gift cards work?',
  answer: 'eGift cards are delivered instantly via email. You can include a personalized message and choose the exact delivery date.'
}, {
  question: 'Can I shop directly on Instagram or TikTok?',
  answer: "Yes! Simply click the 'Shop' tab on our social profiles to browse and checkout without leaving the app."
}, {
  question: 'How can we collaborate with TriLyn?',
  answer: "We love working with creators! Please use the 'Collaboration Request' subject in our contact form or visit our Colabs hub."
}, {
  question: 'Is your jewelry handcrafted?',
  answer: 'Every piece in our Victoria and Zarrito collections is handcrafted by master artisans using authentic materials.'
}];
const FAQItem = ({
  question,
  answer,
  isOpen,
  onClick
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}) => <div className="border-b border-gray-100" data-id="element-646">
    <button onClick={onClick} className="w-full py-6 flex justify-between items-center text-left hover:text-gold transition-colors group" data-id="element-647">
      <span className="font-serif text-lg md:text-xl" data-id="element-648">{question}</span>
      {isOpen ? <ChevronUp size={20} className="text-gold" data-id="element-649" /> : <ChevronDown size={20} className="text-gray-300 group-hover:text-gold" data-id="element-650" />}
    </button>
    <AnimatePresence data-id="element-651">
      {isOpen && <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} className="overflow-hidden" data-id="element-652">
          <p className="pb-8 text-gray-500 leading-relaxed max-w-3xl" data-id="element-653">
            {answer}
          </p>
        </motion.div>}
    </AnimatePresence>
  </div>;
export function Contact() {
  useScreenInit();
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  return <div className="min-h-screen bg-white" data-id="element-654">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden" data-id="element-655">
        <div className="absolute inset-0" data-id="element-656">
          <img src="https://cdn.magicpatterns.com/uploads/dnFBiLf42a99KfYUr626ET/pasted-image.jpg" className="w-full h-full object-cover" alt="Contact Hero" data-id="element-657" />
          <div className="absolute inset-0 bg-black/60" data-id="element-658" />
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center text-white" data-id="element-659">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-660">
            <span className="text-gold uppercase tracking-[0.4em] text-[10px] font-bold block mb-6" data-id="element-661">
              Get In Touch
            </span>
            <h1 className="text-5xl md:text-7xl font-serif mb-8 leading-tight" data-id="element-662">
              We're Here <br data-id="element-663" />
              to Assist You
            </h1>
            <p className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed font-light" data-id="element-664">
              Have a question about an order, a product, or a potential
              collaboration? Our dedicated team is ready to provide the luxury
              service you deserve.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-24 bg-zinc-50/50" data-id="element-665">
        <div className="container mx-auto px-4" data-id="element-666">
          <div className="flex flex-col lg:flex-row gap-20" data-id="element-667">
            {/* Contact Info */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} viewport={{
            once: true
          }} className="w-full lg:w-1/3 space-y-12" data-id="element-668">
              <div className="space-y-8" data-id="element-669">
                <h2 className="text-3xl font-serif" data-id="element-670">Contact Information</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6" data-id="element-671">
                  <div className="flex items-start gap-4 p-6 bg-white border border-gray-100 shadow-sm" data-id="element-672">
                    <div className="p-3 bg-gray-50 rounded-lg text-gold" data-id="element-673">
                      <MapPin size={20} data-id="element-674" />
                    </div>
                    <div data-id="element-675">
                      <h4 className="font-bold text-xs uppercase tracking-widest mb-1" data-id="element-676">
                        Our Studio
                      </h4>
                      <p className="text-gray-500 text-sm" data-id="element-677">
                        123 Heritage Way, Suite 400
                        <br data-id="element-678" />
                        Culture District, NY 10012
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-6 bg-white border border-gray-100 shadow-sm" data-id="element-679">
                    <div className="p-3 bg-gray-50 rounded-lg text-gold" data-id="element-680">
                      <Phone size={20} data-id="element-681" />
                    </div>
                    <div data-id="element-682">
                      <h4 className="font-bold text-xs uppercase tracking-widest mb-1" data-id="element-683">
                        Phone
                      </h4>
                      <p className="text-gray-500 text-sm" data-id="element-684">+1 (555) 123-4567</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-6 bg-white border border-gray-100 shadow-sm" data-id="element-685">
                    <div className="p-3 bg-gray-50 rounded-lg text-gold" data-id="element-686">
                      <Mail size={20} data-id="element-687" />
                    </div>
                    <div data-id="element-688">
                      <h4 className="font-bold text-xs uppercase tracking-widest mb-1" data-id="element-689">
                        Email
                      </h4>
                      <p className="text-gray-500 text-sm" data-id="element-690">
                        hello@shoptrilyn.com
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-6 bg-white border border-gray-100 shadow-sm" data-id="element-691">
                    <div className="p-3 bg-gray-50 rounded-lg text-gold" data-id="element-692">
                      <Clock size={20} data-id="element-693" />
                    </div>
                    <div data-id="element-694">
                      <h4 className="font-bold text-xs uppercase tracking-widest mb-1" data-id="element-695">
                        Hours
                      </h4>
                      <p className="text-gray-500 text-sm" data-id="element-696">
                        Mon - Fri: 9am - 6pm EST
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6" data-id="element-697">
                <h4 className="font-bold text-xs uppercase tracking-widest" data-id="element-698">
                  Follow Our Journey
                </h4>
                <div className="flex gap-4" data-id="element-699">
                  <button className="p-4 bg-gray-50 rounded-xl hover:bg-black hover:text-white transition-all duration-300" data-id="element-700">
                    <Instagram size={20} data-id="element-701" />
                  </button>
                  <button className="p-4 bg-gray-50 rounded-xl hover:bg-black hover:text-white transition-all duration-300" data-id="element-702">
                    <TikTokIcon size={20} data-id="element-703" />
                  </button>
                  <button className="p-4 bg-gray-50 rounded-xl hover:bg-black hover:text-white transition-all duration-300" data-id="element-704">
                    <Facebook size={20} data-id="element-705" />
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} viewport={{
            once: true
          }} className="w-full lg:w-2/3 bg-white border border-gray-100 p-8 md:p-12 shadow-xl" data-id="element-706">
              <form className="space-y-8" onSubmit={e => e.preventDefault()} data-id="element-707">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-708">
                  <div className="space-y-2" data-id="element-709">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-gray-400" data-id="element-710">
                      First Name
                    </label>
                    <input type="text" className="w-full border-b border-gray-200 py-3 focus:outline-none focus:border-gold transition-colors text-sm" data-id="element-711" />
                  </div>
                  <div className="space-y-2" data-id="element-712">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-gray-400" data-id="element-713">
                      Last Name
                    </label>
                    <input type="text" className="w-full border-b border-gray-200 py-3 focus:outline-none focus:border-gold transition-colors text-sm" data-id="element-714" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-715">
                  <div className="space-y-2" data-id="element-716">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-gray-400" data-id="element-717">
                      Email Address
                    </label>
                    <input type="email" className="w-full border-b border-gray-200 py-3 focus:outline-none focus:border-gold transition-colors text-sm" data-id="element-718" />
                  </div>
                  <div className="space-y-2" data-id="element-719">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-gray-400" data-id="element-720">
                      Phone Number
                    </label>
                    <input type="tel" className="w-full border-b border-gray-200 py-3 focus:outline-none focus:border-gold transition-colors text-sm" data-id="element-721" />
                  </div>
                </div>

                <div className="space-y-2" data-id="element-722">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-gray-400" data-id="element-723">
                    Subject
                  </label>
                  <select className="w-full border-b border-gray-200 py-3 focus:outline-none focus:border-gold transition-colors text-sm bg-transparent cursor-pointer appearance-none" data-id="element-724">
                    <option data-id="element-725">General Inquiry</option>
                    <option data-id="element-726">Order Question</option>
                    <option data-id="element-727">Product Question</option>
                    <option data-id="element-728">Gift Card Question</option>
                    <option data-id="element-729">Collaboration Request</option>
                    <option data-id="element-730">Vendor Event</option>
                    <option data-id="element-731">Social Shop Question</option>
                  </select>
                </div>

                <div className="space-y-2" data-id="element-732">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-gray-400" data-id="element-733">
                    Message
                  </label>
                  <textarea rows={5} className="w-full border border-gray-100 bg-gray-50/50 p-4 focus:outline-none focus:border-gold transition-colors text-sm resize-none" placeholder="How can we help you today?" data-id="element-734"></textarea>
                </div>

                <button className="bg-black text-white px-12 py-4 text-xs uppercase tracking-widest font-bold hover:bg-gold transition-all duration-300 flex items-center justify-center gap-3 shadow-lg" data-id="element-735">
                  Send Message <Send size={16} data-id="element-736" />
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-50" data-id="element-737">
        <div className="container mx-auto px-4" data-id="element-738">
          <div className="max-w-4xl mx-auto" data-id="element-739">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} viewport={{
            once: true
          }} className="text-center mb-16" data-id="element-740">
              <h2 className="text-3xl md:text-5xl font-serif mb-4 leading-tight" data-id="element-741">
                Frequently Asked Questions
              </h2>
              <div className="w-20 h-1 bg-gold mx-auto" data-id="element-742"></div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} viewport={{
            once: true
          }} className="bg-white p-8 md:p-12 shadow-sm border border-gray-100" data-id="element-743">
              {faqData.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} isOpen={openFaq === index} onClick={() => setOpenFaq(openFaq === index ? null : index)} data-id="element-744" />)}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Support CTA */}
      <section className="py-20 bg-black text-white" data-id="element-745">
        <div className="container mx-auto px-4 text-center" data-id="element-746">
          <div className="max-w-2xl mx-auto space-y-8" data-id="element-747">
            <div className="inline-flex p-4 bg-zinc-900 rounded-full text-gold mb-4" data-id="element-748">
              <MessageCircle size={32} data-id="element-749" />
            </div>
            <h2 className="text-3xl font-serif" data-id="element-750">Need immediate assistance?</h2>
            <p className="text-gray-400" data-id="element-751">
              Our customer support team is available via live chat during
              business hours for quick answers to your questions.
            </p>
            <button className="bg-white text-black px-10 py-4 text-xs uppercase tracking-widest font-bold hover:bg-gold transition-all duration-300" data-id="element-752">
              Start Live Chat
            </button>
          </div>
        </div>
      </section>
    </div>;
}